import { Button, Checkbox, Dialog, DialogTitle, DialogContent, TextField, Autocomplete, DialogActions, Box, Typography, RadioGroup, FormControlLabel, Radio, Table, TableBody, TableRow, TableCell, Divider } from "@mui/material";
import { TrackEncoder } from "@nutilogi/trzip";
import dayjs from "dayjs";
import { set, get, getDatabase, ref, update } from "firebase/database";
import {
    getStorage,
    ref as storageRef,
    uploadBytes,
} from "firebase/storage"; import React, { useState, useEffect, useMemo, useCallback } from "react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import store, { useTSelector } from "rx/store";
import { addPoints, TrackPoint, trackToPoints } from "rx/tracksSlice";

type SingleTeamAnswers = ReturnType<typeof store.getState>['teamAnswers'][string][string];

function mergeTracks(sourcetracks: TrackPoint[][], starttime?: number, finishtime?: number): TrackPoint[] {
    // Sort tracks by their first timestamp
    const tracks = sourcetracks
        .filter(track => track.length > 0)
        .sort((a, b) => a[0].t - b[0].t)
        .map(t => ({ tp: 0, track: t }));

    let result: TrackPoint[] = [];

    if (tracks.length === 0) return result;

    let currenttrack = 0;
    let lastp = tracks[currenttrack].track[tracks[currenttrack].tp++];

    result.push(lastp);

    const timediff = 5000;

    while (true) {
        const ct = tracks[currenttrack];
        if (ct.tp < ct.track.length && (ct.track[ct.tp].t - lastp.t) < timediff) {
            lastp = ct.track[ct.tp++];
            result.push(lastp);
            continue;
        }

        let candidates: { t: number; trackIdx: number }[] = [];
        for (let i = 0; i < tracks.length; i++) {
            while (tracks[i].tp < tracks[i].track.length && tracks[i].track[tracks[i].tp].t < lastp.t) {
                tracks[i].tp++;
            }
            if (tracks[i].tp < tracks[i].track.length) {
                candidates.push({ t: tracks[i].track[tracks[i].tp].t, trackIdx: i });
            }
        }
        if (candidates.length === 0) break;

        candidates.sort((a, b) => a.t - b.t);
        const { t: nextTime, trackIdx: nextTrack } = candidates[0];

        if (
            ct.tp >= ct.track.length ||
            nextTime - lastp.t < timediff
        ) {
            currenttrack = nextTrack;
        }

        if (tracks[currenttrack].tp >= tracks[currenttrack].track.length) break;
        lastp = tracks[currenttrack].track[tracks[currenttrack].tp++];
        result.push(lastp);
    }
    if (starttime) {
        while (result.length > 0 && result[0].t < starttime) result.shift();
    }
    if (finishtime) {
        while (result.length > 0 && result[result.length - 1].t > finishtime) result.pop();
    }
    return result;
}

// Conflict's type could be refactored to group conflicts on different objects if need arises
const useDoJoin = () => {
    const teams = useTSelector(state => state.teamsList);
    const evid = useTSelector(state => state.eventId);
    const dispatch = useDispatch();

    return async (newTeamName: string, t1id: string, t2id: string, disableJoinedTeams: boolean, conflictChoices: { [key: string]: "t1" | "t2" }) => {
        const t1 = teams[t1id];
        const t2 = teams[t2id];

        //console.log("doJoin", conflicts, conflictChoices);

        const db = getDatabase();
        let usec = 3;
        let nkey = "";
        let exists = true;

        while (exists) {
            nkey = `${newTeamName}_${t1id.substring(0, usec)}_${t2id.substring(0, usec)}`;
            const snap = await get(ref(db, `/teams/${evid}/list/${nkey}`));
            exists = snap.exists();
            usec++;
        }

        const listentry = {
            starttime: (t1.starttime || t2.starttime) ? Math.min(t1.starttime ?? Number.MAX_SAFE_INTEGER, t2.starttime ?? Number.MAX_SAFE_INTEGER) : null,
            finishtime: (t1.finishtime || t2.finishtime) ? Math.max(t1.finishtime ?? 0, t2.finishtime ?? 0) : null,
            devs: {
                generated: "X"
            },
            name: newTeamName
        }

        const teamAnswers = store.getState().teamAnswers;
        const t1answers = teamAnswers[t1id];
        const t2answers = teamAnswers[t2id];

        const overlappingKeys = Object.keys(t1answers).filter(key => key in t2answers && JSON.stringify(t1answers[key]) !== JSON.stringify(t2answers[key]));
        let haveunresolvedkpconflict = false;
        overlappingKeys.forEach(v => {
            if (!conflictChoices[v]) haveunresolvedkpconflict = true;
        })
        if (overlappingKeys.length > 0 && haveunresolvedkpconflict) {
            return overlappingKeys.map(key => ({
                key,
                t1: t1answers[key],
                t2: t2answers[key]
            }));
        }

        const newkpanswers = Object.assign({}, t1answers, t2answers)
        Object.entries(conflictChoices).forEach(([v, wteam]) => {
            newkpanswers[v] = wteam === "t1" ? t1answers[v] : t2answers[v];
        })

        const tracks = store.getState().tracks;
        const alltracks: TrackPoint[][] = [];
        [t1id, t2id].forEach(tid => Object.values(tracks[tid] ?? {}).forEach(v => {
            alltracks.push(v);
        }));

        /*
        const allPoints = alltracks.flat();
        const pointMap = new Map<number, TrackPoint>();
        allPoints.forEach(point => {
            pointMap.set(point.t, point);
        });
        const finaltrack = Array.from(pointMap.values()).sort((a, b) => a.t - b.t);
        */
        const finaltrack = mergeTracks(alltracks, listentry.starttime || undefined, listentry.finishtime || undefined);

        const encoder = new TrackEncoder({ tid: nkey, devid: "generated" });
        encoder.addPoints(trackToPoints(finaltrack));
        const trackbuffer = encoder.encodedBuffer();
        dispatch(addPoints({ tid: nkey, devid: "generated", points: trackToPoints(finaltrack) }));

        await set(ref(db, `/teams/${evid}/list/${nkey}`), listentry);
        await set(ref(db, `/teams/${evid}/kpanswers/${nkey}`), newkpanswers);

        const upref = storageRef(getStorage(), `/nutilogi/events/${evid}/${nkey}_generated_ws.data`);
        await uploadBytes(upref, trackbuffer, {
            contentType: "application/octet-stream",
            customMetadata: {
                team1: "t1name",
                team2: "t2name",
                generated: "true"
            }
        });
        if (disableJoinedTeams) {
            await update(ref(db, `/teams/${evid}/list`), {
                [`${t1id}/disabled`]: true,
                [`${t2id}/disabled`]: true,
            });
        }

        return;
    };
}

const JoinDialog: React.FC<{ setIsOpen: (b: boolean) => void }> = ({ setIsOpen }) => {
    const teams = useTSelector(state => state.teamsList);
    const kpList = useTSelector(state => state.kpList);
    const kpData = useTSelector(state => state.kpData);
    const doJoin = useDoJoin();
    const [pendingJoinAfterConflicts, setPendingJoinAfterConflicts] = useState(false);
    const [disableOriginalTeams, setDisableOriginalTeams] = useState(true);


    const { t } = useTranslation();
    const [conflicts, setConflicts] = useState<{ key: string, t1: SingleTeamAnswers, t2: SingleTeamAnswers }[]>([]);

    const [conflictChoices, setConflictChoices] = useState<{ [key: string]: "t1" | "t2" }>({});
    const [showConflictDialog, setShowConflictDialog] = useState(false);

    type TeamOption = { id: string; name: string } | null;
    const teamOptions = useMemo(() =>
        Object.entries(teams)
            .filter(([_, team]) => !team.disabled && team.name)
            .map(([id, team]) => ({ id, name: team.name! })),
        [teams]
    );


    const [team1, setTeam1] = useState<TeamOption>();
    const [team2, setTeam2] = useState<TeamOption>();
    const [newTeamName, setNewTeamName] = useState("");

    const handleClose = useCallback(() => {
        setIsOpen(false);
        setTeam1(null);
        setTeam2(null);
        setConflictChoices({});
        setNewTeamName("");
    }, [setIsOpen]);

    const invokeJoin = useCallback(() => {
        if (!team1?.id || !team2?.id) return;
        doJoin(newTeamName, team1.id, team2.id, disableOriginalTeams, conflictChoices).then(r => {
            if (r) {
                setConflicts(r);
                setShowConflictDialog(true);
            } else {
                handleClose();
            }
        });
    }, [conflictChoices, disableOriginalTeams, doJoin, handleClose, newTeamName, team1?.id, team2?.id]);

    useEffect(() => {
        if (!showConflictDialog && pendingJoinAfterConflicts) {
            setPendingJoinAfterConflicts(false);
            invokeJoin();
        }
    }, [showConflictDialog, pendingJoinAfterConflicts, doJoin, invokeJoin]);

    const solveKPMapEntry = useCallback((kp: string, k: string, v: string | number | boolean) => {
        if (k === "answer") {
            if (kpData && typeof (v) === "string") {
                let responses = kpData[kp]?.responses;
                if (responses) {
                    return responses[v];
                }
            }
        } else if (k === "aeg" && typeof (v) === "number") {
            return dayjs(v).format("HH:mm:ss");
        }
        return String(v);
    }, [kpData]);

    const handleNewTeamName = (str: string | undefined, otherstr: string | undefined) => {
        if (!str) {
            if (!otherstr) setNewTeamName("");
            return;
        }
        if (newTeamName === "")
            setNewTeamName(str);
    }
    return <>
        <DialogTitle>{t("dialog.title.jointeams")}</DialogTitle>
        <DialogContent>
            <TextField
                label={t("jointeams.newteamname")}
                value={newTeamName}
                onChange={e => setNewTeamName(e.target.value)}
                fullWidth
                margin="normal"
            />
            <Autocomplete
                options={teamOptions.filter(opt => !team2 || opt.id !== team2.id)}
                getOptionLabel={option => option.name}
                value={team1}
                onChange={(_, value) => { setTeam1(value); handleNewTeamName(value?.name, team2?.name); }}
                renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                        {option.name}
                    </li>
                )}
                renderInput={params => (
                    <TextField {...params} label={t("select.firstteam")} margin="normal" />
                )}
            />
            <Autocomplete
                options={teamOptions.filter(opt => !team1 || opt.id !== team1.id)}
                getOptionLabel={option => option.name}
                value={team2}
                onChange={(_, value) => { setTeam2(value); handleNewTeamName(value?.name, team1?.name); }}
                renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                        {option.name}
                    </li>
                )}
                renderInput={params => (
                    <TextField {...params} label={t("select.secondteam")} margin="normal" />
                )}
            />
            <FormControlLabel
                control={
                    <Checkbox
                        checked={disableOriginalTeams}
                        onChange={e => setDisableOriginalTeams(e.target.checked)}
                    />
                }
                label={t("jointeams.disablejoinedTeams")}
            />
        </DialogContent>
        <DialogActions>
            <Button variant="contained" onClick={handleClose}>{t("button.close")}</Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button
                variant="contained"
                color="primary"
                disabled={!team1 || !team2}
                onClick={() => {
                    invokeJoin();
                }}
            >
                {t("button.join")}
            </Button>
        </DialogActions>
        <Dialog open={showConflictDialog} maxWidth="lg" fullWidth fullScreen={isMobile} onClose={() => setShowConflictDialog(false)}>
            <DialogTitle>{t("dialog.conflicts.title")}</DialogTitle>
            <DialogContent>
                {conflicts.map(({ key, t1, t2 }, idx) => (
                    <React.Fragment key={key}>
                        <Box key={key} sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant="subtitle2" sx={{ whiteSpace: "nowrap", paddingRight: 2 }}>{`${t("kplyhend")} ${kpList[key].nr} `}</Typography>
                            <RadioGroup
                                value={conflictChoices[key] || "t2"}
                                onChange={e => setConflictChoices(prev => ({ ...prev, [key]: e.target.value as "t1" | "t2" }))}
                            >
                                <FormControlLabel
                                    value="t1"
                                    control={<Radio />}
                                    label={
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{ fontWeight: 500, mr: 2, mt: 0.5 }}
                                            >
                                                {team1?.name || t("jointeams.team1")}
                                            </Typography>
                                            <Table size="small">
                                                <TableBody>
                                                    {Object.entries(t1).map(([k, v]) => (
                                                        <TableRow key={k}>
                                                            <TableCell sx={{ border: 0, p: 0.5, pr: 2, fontWeight: 500 }}>{k}</TableCell>
                                                            <TableCell sx={{ border: 0, p: 0.5 }}>{solveKPMapEntry(key, k, v)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </Box>
                                    }
                                />
                                <Divider />
                                <FormControlLabel
                                    value="t2"
                                    control={<Radio />}
                                    label={
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{ fontWeight: 500, mr: 2, mt: 0.5 }}
                                            >
                                                {team2?.name || t("jointeams.team2")}
                                            </Typography>
                                            <Table size="small">
                                                <TableBody>
                                                    {Object.entries(t2).map(([k, v]) => (
                                                        <TableRow key={k}>
                                                            <TableCell sx={{ border: 0, p: 0.5, pr: 2, fontWeight: 500 }}>{k}</TableCell>
                                                            <TableCell sx={{ border: 0, p: 0.5 }}>{solveKPMapEntry(key, k, v)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </Box>
                                    }
                                />              </RadioGroup>
                        </Box>
                        {idx < conflicts.length - 1 && <Divider sx={{ my: 2 }} />}
                    </React.Fragment>
                ))}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setShowConflictDialog(false)}>{t("button.cancel")}</Button>
                <Button
                    variant="contained"
                    onClick={async () => {
                        let missingchoices: { [key: string]: "t1" | "t2" } = {};
                        conflicts.forEach(({ key }) => {
                            if (conflictChoices[key]) return;
                            missingchoices[key] = "t2";
                        });
                        setConflictChoices(prev => ({ ...prev, ...missingchoices }));
                        setShowConflictDialog(false);
                        setPendingJoinAfterConflicts(true);
                    }}
                >
                    {t("button.save")}
                </Button>
            </DialogActions>
        </Dialog >
    </>
}
const JoinTeams: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const { t } = useTranslation();

    return (
        <>
            <Button
                variant="contained"
                color="primary"
                onClick={() => setIsOpen(true)}
            >
                {t("button.jointeams")}
            </Button>
            <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
                <JoinDialog setIsOpen={setIsOpen} />
            </Dialog>

        </>
    );
};

export default JoinTeams;